import { Section } from '@/components/layout';
import { RouteLine, Sparkle } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { DestinationCard } from '@/components/ui';
import { DisplayTitle } from '@/components/typography';
import { EVENTS } from '@/features/events';
import { assets, type AssetKey } from '@/assets/manifest';
import { AssetImage } from '@/pages/shared/AssetImage';
import { ROUTE } from '../hub.content';
import styles from './HubRoute.module.css';

/**
 * "La Ruta del Despertar": los 5 destinos de la gira.
 *
 * Los destinos salen de `EVENTS` (ADR-003): esta pagina no teclea ni una ciudad
 * ni una fecha. El orden es el cronologico que valida el esquema Zod, y Cancun
 * se destaca sola porque su `isVip` viene en la config, no porque yo la ponga
 * la ultima a mano.
 */
/**
 * Landmark de cada tarjeta. La clave se deriva del slug, asi que dar de alta un
 * destino nuevo no obliga a tocar esto — pero se comprueba contra el manifiesto
 * antes de usarla: si BRAVO aun no ha generado esa imagen, la tarjeta se pinta
 * sin media en vez de romper la pagina.
 */
function landmarkDe(slug: string) {
  const key = `hub/card-${slug}` as AssetKey;
  return key in assets ? key : null;
}

export function HubRoute() {
  return (
    <Section
      id="destinos"
      background="alt"
      labelledBy="hub-ruta-title"
      width="wide"
      spacing="tight"
    >
      <Reveal>
        {/* EL TITULAR VA FLANQUEADO POR DOS DESTELLOS, no con un filete dorado
            debajo: en el poster es `✦ La Ruta del Despertar ✦`, en versalitas
            pequenas con tracking, y bajo el titular no hay nada — lo siguiente es
            la propia linea de la ruta. El `GoldLine` con rombo que habia aqui era
            un ornamento de mas (ADR-005 §1.1). */}
        <div className={styles.heading}>
          <Sparkle className={styles.headingStar} size={15} />
          <DisplayTitle
            id="hub-ruta-title"
            level={2}
            size="md"
            align="center"
            className={styles.title}
          >
            {ROUTE.title}
          </DisplayTitle>
          <Sparkle className={styles.headingStar} size={15} />
        </div>
      </Reveal>

      {/* Ornamento decorativo: la linea dorada discontinua que enhebra los 5
          destinos. Solo en escritorio (ver .route en el CSS). */}
      <RouteLine className={styles.route} sparkles={EVENTS.length} animated />

      {/* `Reveal as="li"` en cada item, y no un `RevealGroup` alrededor: el grupo
          envuelve cada hijo en un `div`, y un `div` entre `ul` y `li` es HTML
          invalido y rompe la semantica de lista para el lector de pantalla. */}
      <ul className={styles.cards} role="list">
        {EVENTS.map((event, index) => (
          <Reveal as="li" className={styles.item} delay={index * 0.06} key={event.slug}>
            <DestinationCard
              event={event}
              showOrder={event.isVip}
              media={(() => {
                const key = landmarkDe(event.slug);
                return key ? (
                  <AssetImage asset={key} sizes="(min-width: 1024px) 18vw, (min-width: 560px) 45vw, 92vw" />
                ) : undefined;
              })()}
            />
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}
